import { useEffect } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Brand } from "@/components/Brand";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { LayoutDashboard, Edit3, BarChart3, LogOut, Loader2, PawPrint, Map, Users, HeartHandshake, ChevronLeft, ChevronRight, ImageIcon, Menu, Settings, History, Sparkles } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const BetaBadge = () => {
  const content = (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <p className="font-semibold text-sm">Panel w wersji beta</p>
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed">
        Panel organizacji jest obecnie w fazie testów. Dostęp ma wąska grupa schronisk, a my aktywnie zbieramy uwagi i dopracowujemy funkcje.
      </p>
      <p className="text-sm text-muted-foreground leading-relaxed">
        Jeśli coś nie działa lub masz pomysł na ulepszenie - daj nam znać przez widget pomocy w prawym dolnym rogu.
      </p>
    </div>
  );
  const trigger = (
    <button
      type="button"
      className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary hover:bg-primary/15 transition-colors cursor-help shrink-0"
      aria-label="Informacja o wersji beta"
    >
      <Sparkles className="h-3 w-3" />
      Beta
    </button>
  );
  return (
    <>
      <div className="hidden md:block">
        <HoverCard openDelay={100} closeDelay={100}>
          <HoverCardTrigger asChild>{trigger}</HoverCardTrigger>
          <HoverCardContent align="start" className="w-80">
            {content}
          </HoverCardContent>
        </HoverCard>
      </div>
      <div className="md:hidden">
        <Popover>
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
          <PopoverContent align="start" className="w-72">
            {content}
          </PopoverContent>
        </Popover>
      </div>
    </>
  );
};

const MobileMenuButton = () => {
  const { toggleSidebar } = useSidebar();
  return (
    <button
      type="button"
      onClick={toggleSidebar}
      aria-label="Rozwiń menu panelu"
      className="md:hidden h-8 inline-flex items-center gap-1.5 pl-2 pr-2.5 rounded-full border border-primary/30 bg-primary/5 text-primary text-xs font-semibold hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all shadow-sm shrink-0"
    >
      <Menu className="h-3.5 w-3.5" />
      Menu
    </button>
  );
};
import { toast } from "sonner";
import { PanelHelpWidget } from "@/components/PanelHelpWidget";
import { PanelErrorBoundary } from "@/components/panel/PanelErrorBoundary";
import { useOwnedShelters } from "@/hooks/useOwnedShelters";

const buildNavItems = (shelterSlug?: string) => [
  { id: "dashboard", to: "/panel", label: "Pulpit", icon: LayoutDashboard, match: (p: string) => p === "/panel" || p === "/panel/", requiresShelter: false },
  { id: "profile", to: shelterSlug ? `/panel/profil/${shelterSlug}` : "/panel", label: "Profil schroniska", icon: Edit3, match: (p: string) => p.startsWith("/panel/profil"), requiresShelter: true },
  { id: "needs", to: shelterSlug ? `/panel/potrzeby/${shelterSlug}` : "/panel", label: "Potrzeby", icon: HeartHandshake, match: (p: string) => p.startsWith("/panel/potrzeby"), requiresShelter: true },
  { id: "graphics", to: shelterSlug ? `/panel/grafiki/${shelterSlug}` : "/panel", label: "Grafiki", icon: ImageIcon, match: (p: string) => p.startsWith("/panel/grafiki"), requiresShelter: true },
  { id: "statistics", to: shelterSlug ? `/panel/statystyki/${shelterSlug}` : "/panel", label: "Statystyki", icon: BarChart3, match: (p: string) => p.startsWith("/panel/statystyki"), requiresShelter: true },
  { id: "history", to: shelterSlug ? `/panel/historia/${shelterSlug}` : "/panel", label: "Historia zmian", icon: History, match: (p: string) => p.startsWith("/panel/historia"), requiresShelter: true },
  { id: "team", to: "/panel/zespol", label: "Zespół", icon: Users, match: (p: string) => p.startsWith("/panel/zespol"), requiresShelter: true },
  { id: "account", to: "/panel/konto", label: "Konto", icon: Settings, match: (p: string) => p.startsWith("/panel/konto"), requiresShelter: false },
];

const PanelSidebar = () => {
  const { user, signOut } = useAuth();
  const { data: shelters } = useOwnedShelters(user?.id);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { state, toggleSidebar, isMobile, setOpenMobile } = useSidebar();
  const closeOnMobile = () => { if (isMobile) setOpenMobile(false); };
  const collapsed = !isMobile && state === "collapsed";
  const hasShelter = (shelters?.length ?? 0) > 0;
  const navItems = buildNavItems(shelters?.[0]?.slug);

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success("Wylogowano");
      navigate("/", { replace: true });
    } catch {
      toast.error("Nie udało się wylogować");
    }
  };

  const mainItems = navItems.filter((i) => i.id !== "account");
  const accountItem = navItems.find((i) => i.id === "account");
  const disabledTitle = "Najpierw poproś o dostęp do schroniska lub dodaj nowe (na Pulpicie).";

  const renderItem = (item: ReturnType<typeof buildNavItems>[number]) => {
    const isActive = item.match(pathname);
    const isDisabled = item.requiresShelter && !hasShelter;
    return (
      <SidebarMenuItem key={item.id}>
        <SidebarMenuButton
          asChild
          tooltip={isDisabled ? `${item.label} - ${disabledTitle}` : item.label}
          isActive={isActive}
          className="h-auto py-2.5 rounded-2xl data-[active=true]:bg-transparent group-data-[collapsible=icon]:!size-10 group-data-[collapsible=icon]:!p-0 group-data-[collapsible=icon]:!rounded-2xl group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:!justify-center"
        >
          {isDisabled ? (
            <div
              aria-disabled="true"
              title={disabledTitle}
              onClick={(e) => { e.preventDefault(); closeOnMobile(); navigate("/panel"); }}
              className="relative flex items-center gap-3 rounded-2xl text-muted-foreground/60 opacity-60 cursor-not-allowed select-none group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0"
            >
              <item.icon className="h-5 w-5 shrink-0 opacity-70" />
              {!collapsed && (
                <span className="whitespace-nowrap text-sm font-semibold animate-fade-in">
                  {item.label}
                </span>
              )}
            </div>
          ) : (
            <NavLink
              to={item.to}
              onClick={closeOnMobile}
              className={[
                "group relative flex items-center gap-3 rounded-2xl transition-all duration-300",
                "group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0",
                collapsed
                  ? isActive
                    ? "bg-primary text-primary-foreground shadow-md shadow-primary/25 hover:bg-primary hover:text-primary-foreground"
                    : "text-muted-foreground hover:bg-white/60 hover:text-primary"
                  : isActive
                    ? "bg-gradient-to-r from-primary/10 to-transparent text-primary"
                    : "text-muted-foreground hover:bg-white/60 hover:text-primary",
              ].join(" ")}
            >
              {!collapsed && isActive && (
                <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-full bg-primary shadow-[0_0_10px_hsl(var(--primary)/0.45)]" />
              )}
              <item.icon
                className={`h-5 w-5 shrink-0 transition-opacity ${
                  isActive ? "opacity-100" : "opacity-70 group-hover:opacity-100"
                }`}
              />
              {!collapsed && (
                <span
                  className={`whitespace-nowrap text-sm animate-fade-in ${
                    isActive ? "font-bold" : "font-semibold"
                  }`}
                >
                  {item.label}
                </span>
              )}
            </NavLink>
          )}
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar collapsible="icon" className="border-r-0">
      <SidebarContent className="bg-white/70 backdrop-blur-2xl border-r border-white/50 shadow-[0_20px_50px_-20px_hsl(var(--primary)/0.18)]">
        <div className={`transition-[padding] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] ${collapsed ? "px-0 py-3" : "px-5 pt-6 pb-4"}`}>
          <Link
            to="/"
            className={`flex items-center gap-3 min-w-0 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-primary ${collapsed ? "justify-center" : ""}`}
            aria-label="Strona główna GdzieSchronisko"
          >
            <span className={`bg-primary rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-primary/25 transition-all duration-300 ${collapsed ? "w-9 h-9" : "w-10 h-10"}`}>
              <PawPrint className={`text-primary-foreground transition-all ${collapsed ? "w-4 h-4" : "w-5 h-5"}`} />
            </span>
            {!collapsed && (
              <span
                className="font-extrabold text-primary text-xl leading-tight tracking-tight truncate animate-fade-in"
                style={{ letterSpacing: "-0.5px" }}
              >
                GdzieSchronisko
              </span>
            )}
          </Link>
        </div>
        <SidebarGroup className="px-3 py-0 group-data-[collapsible=icon]:!px-0">
          <SidebarGroupContent>
            <SidebarMenu className="gap-1.5">
              {mainItems.map(renderItem)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <div className="mt-auto bg-white/40 backdrop-blur-sm border-t border-white/60">
          <SidebarGroup className="px-3 py-3 group-data-[collapsible=icon]:!px-0">
            <SidebarGroupContent>
              <SidebarMenu className="gap-1">
                {accountItem && renderItem(accountItem)}
                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => { closeOnMobile(); handleSignOut(); }}
                    tooltip="Wyloguj się"
                    className="h-auto py-2.5 rounded-2xl text-destructive/80 hover:text-destructive hover:bg-destructive/10 group-data-[collapsible=icon]:!size-10 group-data-[collapsible=icon]:!p-0 group-data-[collapsible=icon]:!rounded-2xl group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:!justify-center"
                  >
                    <LogOut className="h-5 w-5 shrink-0 opacity-80" />
                    {!collapsed && (
                      <span className="whitespace-nowrap text-sm font-bold animate-fade-in">
                        Wyloguj się
                      </span>
                    )}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          <button
            type="button"
            onClick={toggleSidebar}
            className={
              collapsed
                ? "group mx-auto mb-3 h-8 w-8 flex items-center justify-center rounded-xl border border-border/40 bg-white/60 text-muted-foreground hover:text-primary hover:border-primary/40 hover:bg-primary/5 transition-colors"
                : "group mx-3 mb-3 h-8 flex items-center justify-center gap-2 px-3 rounded-xl bg-primary/5 text-primary/80 hover:text-primary hover:bg-primary/10 transition-colors"
            }
            aria-label={collapsed ? "Rozwiń menu boczne" : "Zwiń menu boczne"}
            title={collapsed ? "Rozwiń menu" : "Zwiń menu"}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            ) : (
              <>
                <ChevronLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5" />
                <span className="text-[11px] font-bold uppercase tracking-wider animate-fade-in">Zwiń menu</span>
              </>
            )}
          </button>
        </div>
      </SidebarContent>
    </Sidebar>
  );
};

const PanelLayout = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      const next = window.location.pathname + window.location.search;
      navigate(`/logowanie?next=${encodeURIComponent(next)}`, { replace: true });
    }
  }, [user, loading, navigate]);

  // Globalne łapanie runtime errorów w panelu (window.onerror + unhandledrejection).
  // Pokazujemy toast z akcją "Zgłoś błąd", która otwiera widget pomocy z prefillem.
  useEffect(() => {
    let lastShownAt = 0;
    const COOLDOWN_MS = 4000;

    const isNoise = (msg: string, fileName?: string | null) => {
      if (!msg) return true;
      // Cross-origin script errors (brak detali, nic z nimi nie zrobimy)
      if (msg === "Script error." || msg === "ResizeObserver loop limit exceeded") return true;
      // Rozszerzenia przeglądarki i zewnętrzne hosty
      if (fileName && /^(chrome-extension:|moz-extension:|safari-extension:)/i.test(fileName)) return true;
      // Pliki nie z naszego origin (np. trackery innej domeny)
      try {
        if (fileName && new URL(fileName, window.location.href).origin !== window.location.origin) return true;
      } catch {}
      return false;
    };

    const isUserFacingFetchFailure = (reason: any) => {
      // Zwykłe błędy 401/403/429 z Supabase/fetch - obsługiwane już przez warstwy UI (toasty),
      // nie chcemy ich dublować jako "Wystąpił błąd w panelu".
      const status = reason?.status ?? reason?.statusCode ?? reason?.context?.status;
      if (status && [401, 403, 404, 409, 422, 429].includes(status)) return true;
      const name = reason?.name || "";
      if (name === "AbortError") return true;
      const msg = String(reason?.message || reason || "");
      if (/AbortError|NetworkError|Failed to fetch|Load failed/i.test(msg)) return true;
      return false;
    };

    const showBugToast = (message: string, stack?: string | null, source = "runtime") => {
      const now = Date.now();
      if (now - lastShownAt < COOLDOWN_MS) return;
      lastShownAt = now;
      // Skróć stack dużo wcześniej, żeby suma message+context+stack nie przekroczyła
      // limitu DB feedback_messages.message (4000 znaków).
      const safeStack = stack ? String(stack).slice(0, 800) : null;
      toast.error("Wystąpił błąd w panelu", {
        description: "Pomóż nam ulepszać stronę - zgłoś problem i dołącz zrzut ekranu.",
        duration: 12000,
        action: {
          label: "Zgłoś błąd",
          onClick: () => {
            window.dispatchEvent(
              new CustomEvent("panel-help:report-bug", {
                detail: { message, stack: safeStack, source, path: window.location.pathname },
              }),
            );
          },
        },
      });
    };
    const onError = (e: ErrorEvent) => {
      // Resource errors (np. <img>, <script>) trafiają tu z e.error === null - ignorujemy.
      if (!e.error && !e.message) return;
      if (isNoise(e.message || "", e.filename || null)) return;
      showBugToast(e.message || "Nieznany błąd", e.error?.stack ?? null, "window.onerror");
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      const reason = e.reason;
      if (isUserFacingFetchFailure(reason)) return;
      const msg = typeof reason === "string" ? reason : reason?.message || "Nieobsłużony błąd Promise";
      if (isNoise(msg)) return;
      showBugToast(msg, reason?.stack ?? null, "unhandledrejection");
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  // Jeśli użytkownik jest już znany, od razu pokazujemy zawartość panelu.
  // Nie czekamy na poboczne sprawdzenia auth, żeby nie tworzyć drugiego etapu ładowania.
  const showAuthLoader = loading && !user;

  return (
    <>
      <Helmet>
        <title>Panel schroniska | GdzieSchronisko</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <SidebarProvider>
        <div className="min-h-screen flex w-full bg-muted/30">
          <PanelSidebar />
          <div className="flex-1 flex flex-col min-w-0">
            <header className="h-12 flex items-center border-b bg-background px-4 gap-3">
              <MobileMenuButton />
              <BetaBadge />
              <span className="hidden sm:block text-sm text-muted-foreground truncate flex-1 min-w-0">
                {user ? `Użytkownik: ${user.email}` : "..."}
              </span>
              <span className="sm:hidden flex-1 min-w-0" />
              <Link
                to="/"
                aria-label="Wróć do mapy"
                className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all shadow-sm hover:shadow-md outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
              >
                <Map className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Wróć do mapy</span>
              </Link>
            </header>
            <main data-panel-scope className="flex-1 p-3 md:p-6 min-w-0 overflow-x-hidden">
              {showAuthLoader ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <PanelErrorBoundary>
                  <Outlet />
                </PanelErrorBoundary>
              )}
            </main>
          </div>
        </div>
        <PanelHelpWidget />
      </SidebarProvider>
    </>
  );
};

export default PanelLayout;
